// ── Tip Manager Library ─────────────────────────────────────────
//
// Data model and localStorage persistence for community tip sessions.
// Sessions are stored locally and optionally synced to Nostr via
// kind 30078 (application-specific data) for cross-device access.
//
// A "session" is a week-long (or custom duration) activity where a host
// tracks tips to multiple participants. Tips accumulate over days and
// can be sent all at once or individually.

export interface TipEntry {
  id: string;               // Unique ID for this tip line item
  recipientPubkey: string;  // Nostr pubkey of the recipient
  amountSats: number;       // Amount in satoshis
  amountUsd: number;        // Amount in USD at time of entry
  reason: string;           // Why this tip was given
  createdAt: number;        // Unix timestamp
  sent: boolean;            // Whether the BCH has been sent
  sentAt?: number;          // When it was sent
  txid?: string;            // BCH transaction ID
}

export type SessionTemplate = 'custom' | 'equal-split' | 'ranked';
export type SessionStatus = 'active' | 'closed';

export interface TipSession {
  id: string;               // Unique session ID
  name: string;             // Session name ("BCH Trivia Night - Week of July 14")
  template: SessionTemplate;
  status: SessionStatus;
  createdAt: number;        // Unix timestamp
  closedAt?: number;
  tips: TipEntry[];
  // For equal-split template
  splitTotal?: number;      // Total pot in sats
  // For ranked template
  rankedPrizes?: number[];  // Prize amounts for 1st, 2nd, 3rd...
}

// ── Storage Keys ────────────────────────────────────────────────
const SESSIONS_KEY = 'bchnostr:tip-sessions';

// ── CRUD Operations ─────────────────────────────────────────────

export function loadSessions(): TipSession[] {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

export function saveSessions(sessions: TipSession[]): void {
  try {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
  } catch {}
}

export function createSession(name: string, template: SessionTemplate = 'custom'): TipSession {
  const session: TipSession = {
    id: generateId(),
    name,
    template,
    status: 'active',
    createdAt: Math.floor(Date.now() / 1000),
    tips: [],
  };
  const sessions = loadSessions();
  sessions.unshift(session);
  saveSessions(sessions);
  return session;
}

export function updateSession(sessionId: string, updates: Partial<TipSession>): TipSession | null {
  const sessions = loadSessions();
  const idx = sessions.findIndex(s => s.id === sessionId);
  if (idx === -1) return null;
  sessions[idx] = { ...sessions[idx], ...updates };
  saveSessions(sessions);
  return sessions[idx];
}

export function deleteSession(sessionId: string): void {
  const sessions = loadSessions().filter(s => s.id !== sessionId);
  saveSessions(sessions);
}

export function duplicateSession(sessionId: string): TipSession | null {
  const sessions = loadSessions();
  const original = sessions.find(s => s.id === sessionId);
  if (!original) return null;

  // Create a new session with the same participant list but no tips
  const uniquePubkeys = [...new Set(original.tips.map(t => t.recipientPubkey))];
  const newSession: TipSession = {
    id: generateId(),
    name: `${original.name} (Copy)`,
    template: original.template,
    status: 'active',
    createdAt: Math.floor(Date.now() / 1000),
    tips: [],
    splitTotal: original.splitTotal,
    rankedPrizes: original.rankedPrizes,
  };

  sessions.unshift(newSession);
  saveSessions(sessions);
  return newSession;
}

// ── Tip Operations ──────────────────────────────────────────────

export function addTip(
  sessionId: string,
  recipientPubkey: string,
  amountSats: number,
  amountUsd: number,
  reason: string = '',
): TipEntry | null {
  const sessions = loadSessions();
  const session = sessions.find(s => s.id === sessionId);
  if (!session) return null;

  const tip: TipEntry = {
    id: generateId(),
    recipientPubkey,
    amountSats,
    amountUsd,
    reason,
    createdAt: Math.floor(Date.now() / 1000),
    sent: false,
  };

  session.tips.push(tip);
  saveSessions(sessions);
  return tip;
}

export function removeTip(sessionId: string, tipId: string): void {
  const sessions = loadSessions();
  const session = sessions.find(s => s.id === sessionId);
  if (!session) return;
  session.tips = session.tips.filter(t => t.id !== tipId);
  saveSessions(sessions);
}

export function markTipSent(sessionId: string, tipId: string, txid?: string): void {
  const sessions = loadSessions();
  const session = sessions.find(s => s.id === sessionId);
  if (!session) return;
  const tip = session.tips.find(t => t.id === tipId);
  if (!tip) return;
  tip.sent = true;
  tip.sentAt = Math.floor(Date.now() / 1000);
  if (txid) tip.txid = txid;
  saveSessions(sessions);
}

export function markTipFailed(sessionId: string, tipId: string): void {
  const sessions = loadSessions();
  const session = sessions.find(s => s.id === sessionId);
  if (!session) return;
  const tip = session.tips.find(t => t.id === tipId);
  if (!tip) return;
  tip.sent = false;
  tip.sentAt = undefined;
  tip.txid = undefined;
  saveSessions(sessions);
}

// ── Aggregation Helpers ─────────────────────────────────────────

export interface ParticipantSummary {
  pubkey: string;
  totalSats: number;
  totalUsd: number;
  tipCount: number;
  tips: TipEntry[];
  allSent: boolean;
  pendingSats: number;
  sentSats: number;
}

export function getParticipantSummaries(session: TipSession): ParticipantSummary[] {
  const byPubkey = new Map<string, TipEntry[]>();
  for (const tip of session.tips) {
    const existing = byPubkey.get(tip.recipientPubkey) || [];
    existing.push(tip);
    byPubkey.set(tip.recipientPubkey, existing);
  }

  const summaries: ParticipantSummary[] = [];
  for (const [pubkey, tips] of byPubkey) {
    const totalSats = tips.reduce((sum, t) => sum + t.amountSats, 0);
    const totalUsd = tips.reduce((sum, t) => sum + t.amountUsd, 0);
    const sentSats = tips.filter(t => t.sent).reduce((sum, t) => sum + t.amountSats, 0);
    const pendingSats = tips.filter(t => !t.sent).reduce((sum, t) => sum + t.amountSats, 0);
    summaries.push({
      pubkey,
      totalSats,
      totalUsd,
      tipCount: tips.length,
      tips: tips.sort((a, b) => b.createdAt - a.createdAt),
      allSent: tips.every(t => t.sent),
      pendingSats,
      sentSats,
    });
  }

  return summaries.sort((a, b) => b.totalSats - a.totalSats);
}

export function getSessionTotals(session: TipSession) {
  const totalSats = session.tips.reduce((sum, t) => sum + t.amountSats, 0);
  const totalUsd = session.tips.reduce((sum, t) => sum + t.amountUsd, 0);
  const pendingSats = session.tips.filter(t => !t.sent).reduce((sum, t) => sum + t.amountSats, 0);
  const sentSats = session.tips.filter(t => t.sent).reduce((sum, t) => sum + t.amountSats, 0);
  const pendingCount = session.tips.filter(t => !t.sent).length;
  const sentCount = session.tips.filter(t => t.sent).length;
  const participantCount = new Set(session.tips.map(t => t.recipientPubkey)).size;

  return { totalSats, totalUsd, pendingSats, sentSats, pendingCount, sentCount, participantCount };
}

// ── Summary Text Generator ──────────────────────────────────────

export function generateSummaryText(
  session: TipSession,
  summaries: ParticipantSummary[],
  profileMap: Map<string, { name?: string; display_name?: string; nip05?: string }>,
): string {
  const lines: string[] = [];
  lines.push(`${session.name} — Results!\n`);

  for (let i = 0; i < summaries.length; i++) {
    const s = summaries[i];
    const profile = profileMap.get(s.pubkey);
    const name = profile?.display_name || profile?.name || s.pubkey.slice(0, 8);
    const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`;
    lines.push(`${medal} @${name} — ${s.totalSats.toLocaleString()} sats`);
  }

  const totals = getSessionTotals(session);
  lines.push(`\nTotal: ${totals.totalSats.toLocaleString()} sats to ${totals.participantCount} participants`);
  lines.push(`\n#bch #bitcoincash #bchnostr`);

  return lines.join('\n');
}

// ── Group tips by day ───────────────────────────────────────────

export function groupTipsByDay(tips: TipEntry[]): Map<string, TipEntry[]> {
  const groups = new Map<string, TipEntry[]>();
  for (const tip of tips) {
    const date = new Date(tip.createdAt * 1000);
    const key = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const existing = groups.get(key) || [];
    existing.push(tip);
    groups.set(key, existing);
  }
  return groups;
}

// ── Utilities ───────────────────────────────────────────────────

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export function formatSats(sats: number): string {
  if (sats >= 100_000_000) return `${(sats / 100_000_000).toFixed(4)} BCH`;
  if (sats >= 1_000_000) return `${(sats / 1_000_000).toFixed(2)}M sats`;
  if (sats >= 1_000) return `${(sats / 1_000).toFixed(1)}k sats`;
  return `${sats.toLocaleString()} sats`;
}

export function timeAgoShort(ts: number): string {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

